import { useContext, useMemo } from 'react';

import { TourKeys } from '@cast/constants';
import {
  Column,
  Icons,
  makeActionsColumn,
  TableHeader,
  useTabsState,
  tokens,
} from '@cast/design-system';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { useAbility } from '@cast/shared-ability-data-access';
import { useIsTourActive } from 'core/tour-v2/hooks/useIsTourActive';
import { usePermitShowAutomatedColumn } from 'features/cluster/workload-optimization/_hooks/permissions/usePermitShowAutomatedColumn';
import { useShowHpaV2 } from 'features/cluster/workload-optimization/_hooks/permissions/useShowHpaV2';
import { useHpaV2Status } from 'features/cluster/workload-optimization/_hooks/useHpaV2Status';
import {
  useIsWoopDemoMode,
  useWorkloadScalingPolicies,
  PreparedWorkload,
} from 'features/workload-optimization';
import { useIsOrganizationReadOnly } from 'hooks/useIsOrganizationReadOnly';
import { useNavigate } from '@cast/infrastructure-organization';

import { CopyToClipboard } from 'components/clickable-actions';
import { canEditWorkload, hasPredictiveWorkloads } from '../../../_utils';
import { OptimizationWorkloadsListContext } from '../../context';
import { useStatusColumnWidth } from '../_hooks/useStatusColumnWidth';
import { WorkloadsTableTab } from '../_types';
import { ActionsMenu } from './ActionsMenu';
import { DifferenceTooltip } from './DifferenceTooltip';
import { HpaSwitch } from './HpaSwitch';
import { OriginalRequestsCell } from './OriginalRequestsCell';
import { PodsCell } from './PodsCell';
import {
  RecommendedMetricsCell,
  RequestedMetricsCell,
} from './ResourceMetricsCell';
import { SavingsTooltip } from './SavingsTooltip';
import { ScalingPolicyCell } from './ScalingPolicyCell';
import { OptimizationTypeCell } from './cells/OptimizationTypeCell';
import { StatusCell } from './cells/StatusCell';
import { WorkloadNameCell } from './cells/WorkloadNameCell/WorkloadNameCell';
import { VpaOptimizationSwitch } from '../../../_components/VpaOptimizationSwitch';
import { DiffCell } from '../../../_workloads-list/components/DiffCell';
import { SavingsCell } from '../../../_workloads-list/components/SavingsCell';
import {
  DiffSorter,
  OriginalRequestsSorter,
  RequestedSorter,
} from '../../../_workloads-list/tableSorters';

import { BodyRowWithTooltip } from './WorkloadsTable/BodyRowWithTooltip';
import { useWorkloadsTableBulkActions } from './WorkloadsTable/useWorkloadsTableBulkActions';
import { PodsSorter, RecommendedSorter } from './WorkloadsTable/sorters';
import { WorkloadsTableBulkActions } from './WorkloadsTable/WorkloadsTableBulkActions';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

import { useTrial } from '@cast/shared-trial-ui';
import { WorkloadConstraintsCell } from './cells/WorkloadConstraintsCell';
import { RecommendedColumnHeader } from './RecommendedColumnHeader';

type WorkloadsTableProps = {
  cacheKey?: string;
  getWorkloadLink?: (workload: PreparedWorkload) => string;
};

export const WorkloadsTable = ({
  cacheKey = tableCacheKeys.WORKLOAD_OPTIMIZATION.WORKLOADS_LIST,
  getWorkloadLink,
}: WorkloadsTableProps = {}) => {
  const { permitShowAutomatedColumn } = usePermitShowAutomatedColumn();

  const { workloads, hasError, refetch, isNamespaceWritable } = useContext(
    OptimizationWorkloadsListContext
  );
  const context = useTabsState<WorkloadsTableTab>();
  const currentTab = context?.currentTab;

  const navigate = useNavigate();

  const accountReadOnly = useIsOrganizationReadOnly();
  const [canEdit] = useAbility().canMany([
    ['update', 'ClusterWorkloadAutoscaler'],
  ]);
  const { showHpaV2 } = useShowHpaV2();
  const { hpaV2Status } = useHpaV2Status();

  const { isEligible } = useTrial();
  const isWoopDemoMode = useIsWoopDemoMode();
  const { policies } = useWorkloadScalingPolicies();
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();
  const isOpTourActive = useIsTourActive(TourKeys.OpinionatedPlatform);

  const {
    selectedIds,
    setSelectedIds,
    toggleOptimization,
    onDisabled,
    offDisabled,
    enablePredictiveWorkloads,
    disablePredictiveWorkloads,
    canDisableEditableWorkloads,
    canEnableWorkloadsCount,
    enableBatchAutoAssignment,
    canEnableAutoAssignmentCount,
  } = useWorkloadsTableBulkActions({ workloads, hpaV2Status });

  const showPredictiveColumn = useMemo(
    () => hasPredictiveWorkloads(workloads),
    [workloads]
  );

  const statusColumnWidth = useStatusColumnWidth(workloads);

  const columns = useMemo(
    () => (
      <>
        <Column<PreparedWorkload>
          id="id"
          header="ID"
          width={31}
          cellWrapperSx={{ pr: 0 }}
          bodyCellSx={{ '& svg': { fontSize: 16, color: 'grey.400' } }}
          renderCell={({ id }) => <CopyToClipboard text={id} />}
          stopClickPropagation
        />
        <Column<PreparedWorkload>
          id="name"
          header="Workload"
          width={200}
          renderCell={(row) => (
            <WorkloadNameCell workload={row} to={getWorkloadLink?.(row)} />
          )}
          cellWrapperSx={{ pl: 20, pr: 12 }}
          sortable
          resizable
          minResizeWidth={70}
        />
        {(currentTab === WorkloadsTableTab.VERTICAL_OPTIMIZATION ||
          showHpaV2) && (
          <Column<PreparedWorkload>
            id="verticalScalingPolicy"
            header="Scaling policy"
            renderCell={(row) => (
              <ScalingPolicyCell
                workload={row}
                disabled={!canEdit || isWoopDemoMode}
                canEdit={canEdit}
                policies={policies ?? []}
                isWoopDemoMode={isWoopDemoMode}
              />
            )}
            minWidth={220}
            sortable
            accessor="verticalScalingPolicy.name"
            stopClickPropagation
            resizable
            minResizeWidth={70}
          />
        )}
        {showHpaV2 && (
          <Column<PreparedWorkload>
            id="optimizationType"
            header="Optimization"
            width={130}
            renderCell={OptimizationTypeCell}
          />
        )}
        <Column<PreparedWorkload>
          id="workloadConstraints"
          header="workload constraints"
          minWidth={210}
          renderCell={(workload) => (
            <WorkloadConstraintsCell workload={workload} />
          )}
          resizable
          minResizeWidth={70}
          visible={false}
        />
        <Column<PreparedWorkload>
          id="namespace"
          header="namespace"
          width={140}
          sortable
          resizable
          minResizeWidth={70}
        />
        <Column<PreparedWorkload>
          id="estimatedMonthlySavings"
          infoTooltip={<SavingsTooltip />}
          header="est. savings/mo"
          headerFullName="Estimated saving per month"
          width={195}
          sortable
          sortingType="number"
          renderCell={({ estimatedMonthlySavings }) => (
            <SavingsCell savings={estimatedMonthlySavings} />
          )}
          resizable
          minResizeWidth={85}
        />

        {currentTab === WorkloadsTableTab.HORIZONTAL_OPTIMIZATION && (
          <>
            <Column<PreparedWorkload>
              id="horizontalOpt"
              header="Automated"
              minWidth={140}
              bodyCellWrapperSx={{
                pl: 44,
              }}
              cellWrapperSx={{
                borderLeft: tokens.theme.border.structural.neutral,
              }}
              renderCell={HpaSwitch}
              sortable
              accessor="isHorizontalScalingEnabled"
              stopClickPropagation
              resizable
              minResizeWidth={70}
            />
            <Column<PreparedWorkload>
              id="podCount-horizontal"
              header="Pod count"
              infoTooltip="Compares current to desired pod count"
              minWidth={144}
              renderCell={PodsCell}
              cellWrapperSx={{
                pr: 8,
                borderRight: tokens.theme.border.structural.neutral,
              }}
              bodyCellSx={{ fontSize: 14 }}
              Sorter={PodsSorter}
              sortingType="number"
              sortable
              resizable
              minResizeWidth={85}
            />
          </>
        )}
        {(currentTab === WorkloadsTableTab.VERTICAL_OPTIMIZATION ||
          showHpaV2) && (
          <>
            {permitShowAutomatedColumn && !showHpaV2 && (
              <Column<PreparedWorkload>
                id="verticalOpt"
                header="Automated"
                width={140}
                renderCell={(row) => (
                  <VpaOptimizationSwitch
                    key={row.id}
                    workload={row}
                    disabled={!canEdit || isWoopDemoMode}
                  />
                )}
                bodyCellWrapperSx={{
                  pl: 44,
                }}
                cellWrapperSx={{
                  borderLeft: tokens.theme.border.structural.neutral,
                }}
                sortable
                accessor="isVerticalScalingEnabled"
                stopClickPropagation
                resizable
                minResizeWidth={70}
              />
            )}

            <Column<PreparedWorkload>
              id="originalRequests"
              header="Orig. Requests"
              headerFullName="Original requests"
              width={160}
              renderCell={OriginalRequestsCell}
              Sorter={OriginalRequestsSorter}
              sortable
              resizable
              minResizeWidth={70}
              visible={false}
            />

            {/* Following two columns are meant to look like one single column (separated due to sorters) */}
            <Column<PreparedWorkload>
              id="requested"
              header="Req."
              headerFullName="Requested"
              width={67}
              renderCell={RequestedMetricsCell}
              cellWrapperSx={{ pr: 0 }}
              Sorter={RequestedSorter}
              sortable
            />
            <Column<PreparedWorkload>
              id="recommended"
              header={<RecommendedColumnHeader />}
              headerFullName="Recommended"
              infoTooltip="Compares current resource requests with the recommended values"
              minWidth={150}
              cellWrapperSx={{ pl: 0 }}
              renderCell={RecommendedMetricsCell}
              Sorter={RecommendedSorter}
              sortable
              resizable
              minResizeWidth={65}
            />
            <Column<PreparedWorkload>
              id="podCount-vertical"
              header="Pod count"
              infoTooltip="Compares current to desired pod count"
              minWidth={144}
              renderCell={PodsCell}
              bodyCellSx={{ fontSize: 14 }}
              Sorter={PodsSorter}
              sortingType="number"
              sortable
              resizable
              minResizeWidth={85}
            />
            <Column<PreparedWorkload>
              id="diff"
              header="Difference"
              infoTooltip={<DifferenceTooltip />}
              minWidth={135}
              renderCell={(it) => <DiffCell workload={it} diffKey="diff" />}
              cellWrapperSx={{
                borderRight: tokens.theme.border.structural.neutral,
              }}
              sortable
              Sorter={DiffSorter}
              resizable
              minResizeWidth={70}
            />
            <Column<PreparedWorkload>
              id="status"
              header="Status"
              width={statusColumnWidth}
              renderCell={StatusCell}
            />
          </>
        )}
        {!isOpTourActive &&
          makeActionsColumn<PreparedWorkload>({
            action: (workload) => {
              return <ActionsMenu workloadId={workload.id} />;
            },
            width: 72,
            cellSx: {
              '& .DS-Table-ActionIcon': {
                width: 40,
                height: 40,
              },
            },
          })}
      </>
    ),
    [
      currentTab,
      showHpaV2,
      permitShowAutomatedColumn,
      canEdit,
      isWoopDemoMode,
      policies,
      statusColumnWidth,
      isOpTourActive,
      getWorkloadLink,
    ]
  );

  return (
    <SearchableTable
      maxHeight="80vh"
      rowHeight={48}
      hasFailed={hasError}
      components={{
        noData: <NoData icon={<Icons.CastNode />} entity="workload" />,
        noResults: <NoResults icon={<Icons.CastNode />} entity="workload" />,
        failed: (
          <FailedToLoad title="Failed to load workloads" refresh={refetch} />
        ),
      }}
      cacheKey={cacheKey}
      outerHeader={
        <TableHeader
          recordsCount={
            <SearchableTableRecordsCount
              count={selectedIds.length}
              title="workload"
            />
          }
          bulkActions={
            <WorkloadsTableBulkActions
              workloads={workloads}
              showHpaV2={showHpaV2}
              isWoopDemoMode={isWoopDemoMode}
              hpaV2Status={hpaV2Status}
              selectedIds={selectedIds}
              toggleOptimization={toggleOptimization}
              onDisabled={onDisabled}
              offDisabled={offDisabled}
              enableBatchAutoAssignment={enableBatchAutoAssignment}
              canEnableAutoAssignmentCount={canEnableAutoAssignmentCount}
              enablePredictiveWorkloads={enablePredictiveWorkloads}
              disablePredictiveWorkloads={disablePredictiveWorkloads}
              canEnableWorkloadsCount={canEnableWorkloadsCount}
              canDisableEditableWorkloads={canDisableEditableWorkloads}
              showPredictiveColumn={showPredictiveColumn}
            />
          }
        />
      }
      BodyRowComponent={BodyRowWithTooltip}
      isRowSelectable={(workload) =>
        canEditWorkload(workload) && isNamespaceWritable(workload.namespace)
      }
      selectedRows={
        canEdit && !isSimulatedClusterDemoUi && !isOpTourActive
          ? selectedIds
          : undefined
      }
      onRowsSelected={
        canEdit && !isSimulatedClusterDemoUi && !isOpTourActive
          ? setSelectedIds
          : undefined
      }
      checkboxColumnProps={{
        width: 24,
        cellWrapperSx: {
          pl: 8,
          pr: 0,
        },
      }}
      onRowClick={
        isOpTourActive
          ? undefined
          : (event, { row }) =>
              navigate(getWorkloadLink?.(row) ?? row.id, { clickEvent: event })
      }
      sx={{
        '& .DS-Table-HeaderRowWrapper': {
          height: 52,
        },
        '& .disabled-row .DS-Table-BodyRowWrapper': {
          opacity: 1,
          '& .DS-Table-CellWrapper': {
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
          },
        },
        '& .disabled-row .DS-Table-rowSelectionDisabled::after': {
          display: 'none',
        },
        '& .woop-premium-value':
          isEligible && accountReadOnly
            ? {
                filter: 'blur(4px)',
              }
            : {},
      }}
      testId="woop-table"
    >
      {columns}
    </SearchableTable>
  );
};
